import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "../lib/leadsApi";

// Public URL to the eBikeGo Cycle Catalog 2026-27
const BROCHURE_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_bike-catalog-9/artifacts/2cy7tz38_eBikeGo%20Cycle%20Catalog-5_260727_194223.pdf";
const BROCHURE_FILENAME = "eBikeGo-Cycle-Catalog-2026-27.pdf";

async function forceDownload(url, filename) {
  try {
    const res = await fetch(url, { mode: "cors" });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
    return true;
  } catch (e) {
    // Fallback: open in a new tab
    window.open(url, "_blank", "noopener,noreferrer");
    return false;
  }
}

export default function BrochureDialog({ open, onClose, productName }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", message: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!/^[0-9+ \-]{7,}$/.test(form.phone.trim())) return toast.error("Please enter a valid mobile number.");

    setBusy(true);
    try {
      await submitLead({
        type: "brochure",
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        city: form.city.trim(),
        message: form.message.trim(),
        model: productName || "",
      });
      await forceDownload(BROCHURE_URL, BROCHURE_FILENAME);
      toast.success("Thanks! Your brochure is downloading.");
      setForm({ name: "", phone: "", email: "", city: "", message: "" });
      onClose?.();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="brochure-dialog"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden shadow-2xl"
          >
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="brochure-close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400 inline-flex items-center justify-center transition"
            >
              <X size={16} />
            </button>

            <div className="px-8 pt-8 pb-6 border-b border-slate-100">
              <span className="w-11 h-11 rounded-2xl bg-[#EAF1FE] text-[#1E6FE5] inline-flex items-center justify-center">
                <FileText size={20} />
              </span>
              <h3 className="font-outfit text-2xl sm:text-3xl font-bold mt-4 text-slate-900 leading-tight">
                Download the eBikeGo Catalog
              </h3>
              <p className="text-sm text-slate-500 mt-2">
                {productName
                  ? `Full ${productName} specs, colours and the complete 2026-27 line-up.`
                  : `Full specs, colours and the complete 2026-27 line-up.`}
              </p>
            </div>

            <form onSubmit={submit} className="px-8 py-6 space-y-4" data-testid="brochure-form">
              <Field label="Full name" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  data-testid="brochure-name"
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2A8BFF] transition"
                  placeholder="Your name"
                  autoFocus
                />
              </Field>
              <Field label="Mobile" required>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  data-testid="brochure-phone"
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2A8BFF] transition"
                  placeholder="+91 98xxxxxxxx"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Email">
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="brochure-email"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2A8BFF] transition"
                    placeholder="Optional"
                  />
                </Field>
                <Field label="City">
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                    data-testid="brochure-city"
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2A8BFF] transition"
                    placeholder="Optional"
                  />
                </Field>
              </div>
              <Field label="Message">
                <textarea
                  rows={2}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  data-testid="brochure-message"
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#2A8BFF] transition resize-none"
                  placeholder="Optional — tell us what you're looking for"
                />
              </Field>

              <button
                type="submit"
                disabled={busy}
                data-testid="brochure-submit"
                className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#2A8BFF] hover:bg-[#1E6FE5] text-white font-bold text-sm uppercase tracking-[0.06em] px-6 py-3.5 rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Preparing…
                  </>
                ) : (
                  <>
                    <Download size={16} /> Download brochure
                  </>
                )}
              </button>
              <p className="text-[11px] text-slate-400 text-center">
                We&apos;ll only use your details to send product updates. No spam.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-bold">
        {label} {required && <span className="text-[#2A8BFF]">*</span>}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
